import { Paging } from '@Core';
import { Sort } from '@components/tables';
import { pushError } from '@components/toast';
import { AxiosError } from '@controllers/axiosClient';
import { contraventionAuditEventHttpController } from '@controllers/index';
import { Pagination as MuiPagination, Stack } from '@mui/material';
import { Filter } from '@pages/cameras/list';
import { OwnFilter } from '@pages/overview/components/CancellationReasons';
import { useState } from 'react';
import { useQuery } from 'react-query';
import { useDebounce } from 'usehooks-ts';
import FilterEventLogComponents from './components/FilterComponents';
import ListEventLogTable from './components/ListEventLogTable';
import { ContraventionAuditEvent } from '@EcamModel/model/ContraventionAuditEvent';
import { usePotentialContravention } from '../../hooks/PotentialContraventionProvider';

export default function RecordedPCNEventLogScreen() {
    const { contraventionId } = usePotentialContravention();

    const [filter, setFilter] = useState<Filter>({
        page: 1,
        from: undefined,
        to: undefined,
        search: '',
    });

    const [filterSortState, setFilterSortState] = useState<OwnFilter>({ sorts: ['-Updated'] } as OwnFilter);
    const debouncedValue = useDebounce<string>(filter.search ?? '', 500);

    const setPartialEventLog = (e: Partial<Filter>) => {
        setFilter((prev) => ({ ...prev, ...e, page: 1 }));
    };

    const handleResetFilter = () => {
        setFilter({
            page: 1,
            from: undefined,
            to: undefined,
            search: '',
            verifierEventLog: undefined,
        });
    };

    const handleSortChange = (sort: Sort) => {
        setFilterSortState((prev) => ({
            ...prev,
            sorts: [sort === 'ASC' ? 'Updated' : '-Updated'],
        }));
    };

    const getTrailAuditEventLogPCN = async (): Promise<Paging<ContraventionAuditEvent>> => {
        const response = await contraventionAuditEventHttpController.list({
            page: filter.page ?? 1,
            From: filter.from ?? undefined,
            To: filter.to ?? undefined,
            pageSize: 10,
            search: {
                content: filter.search ?? '',
                fields: ['UpdatedBy'],
            },
            filter: {
                AuditEventType: filter.verifierEventLog?.Value ?? undefined,
                EntityName: filter.verifierEventLog?.GroupTextKey ?? undefined,
                GroupPotentialContraventionId: contraventionId,
            },
            sorts: filterSortState.sorts as any,
        });
        return response;
    };

    const {
        data: trailAuditEventLogPCN,
        error: errorTrailAuditEventLogPCN,
        isLoading: isLoadingTrailAuditEventLogPCN,
    } = useQuery<Paging<ContraventionAuditEvent>, AxiosError>(
        [
            'trailAuditEventLogPCN',
            contraventionId,
            filter.page,
            filter.from,
            filter.to,
            filter.verifierEventLog,
            filterSortState.sorts,
            debouncedValue,
        ],
        getTrailAuditEventLogPCN,
        {
            staleTime: 5 * 60 * 1000,
            refetchOnWindowFocus: false,
            onError: (err) => {
                pushError(err?.message);
            },
        }
    );

    return (
        <Stack
            direction={'column'}
            spacing={2}
            padding={2}
            borderRadius={'0px 16px 16px 16px'}
            border={'1px solid #E4E7EC'}
        >
            <FilterEventLogComponents
                filter={filter}
                onSetPartialEventLog={setPartialEventLog}
                handleResetFilter={handleResetFilter}
            />

            <ListEventLogTable
                trailAuditEventLogPCN={trailAuditEventLogPCN}
                filterSortState={filterSortState}
                isLoading={isLoadingTrailAuditEventLogPCN}
                handleSortChange={handleSortChange}
            />

            {trailAuditEventLogPCN?.total! > trailAuditEventLogPCN?.pageSize! && !isLoadingTrailAuditEventLogPCN && (
                <Stack mt={3} direction="row" justifyContent="center">
                    <MuiPagination
                        count={trailAuditEventLogPCN?.totalPages || 1}
                        page={trailAuditEventLogPCN?.page}
                        onChange={(_, page) => setFilter((prev) => ({ ...prev, page: page }))}
                        variant="outlined"
                        shape="rounded"
                    />
                </Stack>
            )}
        </Stack>
    );
}
